import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TcThemeService } from './shared/tc-theme/tc-theme.service';
import { GoogleTagService } from './shared/google-tag/google-tag.service';
import { CustomerSessionService } from './core/customer/customer-session.service';
import { CustomerProfileStore } from './core/customer/customer-profile.store';
import { HeaderConfigService } from './shared/tc-app-header/header-config.service';
import { TcAppHeaderComponent } from './shared/tc-app-header/tc-app-header.component';
import { accountChipVisible } from './shared/tc-account-chip/tc-account-chip.component';
import { FulfilmentService } from './core/delivery/fulfilment.service';

/** Static header config a route declares via `data: { header: {...} }`. */
interface RouteHeader {
  show: boolean;
  showBack: boolean;
  backAriaLabel: string;
  title: string | null;
  subtitle: string | null;
  /** Route commands relative to ['/t', token] for the header back button. */
  backHref: string[] | null;
}

const NO_HEADER: RouteHeader = {
  show: false, showBack: true, backAriaLabel: 'Back', title: null, subtitle: null, backHref: null,
};

/**
 * Root component for the TableConnect guest SPA.
 *
 * **TC-THEME wiring.** Every route lives under `/t/:publicToken/...`. We watch
 * navigation and bind the active token to the theme service so the store's
 * brand colour lands on the document root on any deep-link.
 *
 * **TC-A1 global header.** The same navigation pass also binds the customer
 * session + warms the profile cache for the active token, and reads the route's
 * `data.header` to render a single global {@link TcAppHeaderComponent} above the
 * outlet on browse screens (it stays hidden on flow/detail screens that own a
 * back button, and on token-less URLs). One subscription drives all of it.
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, TcAppHeaderComponent],
  template: `
    @if (showHeader()) {
      <tc-app-header
        [showBack]="headerCfg().showBack"
        [backAriaLabel]="headerCfg().backAriaLabel"
        [title]="headerCfg().title"
        [subtitle]="headerCfg().subtitle"
        [publicToken]="publicToken()"
        (back)="onHeaderBack()" />
    }
    <router-outlet />
  `,
})
export class AppComponent implements OnInit {
  private router = inject(Router);
  private theme = inject(TcThemeService);
  private googleTag = inject(GoogleTagService);
  private session = inject(CustomerSessionService);
  private profile = inject(CustomerProfileStore);
  private headerConfigSvc = inject(HeaderConfigService);
  private fulfilment = inject(FulfilmentService);
  private destroyRef = inject(DestroyRef);

  readonly publicToken = signal<string | null>(null);
  private readonly routeHeader = signal<RouteHeader>(NO_HEADER);

  /**
   * TC-A1b — the bar is chrome for its contents, not decoration, so it renders
   * only when at least one of its three slots has something in it.
   *
   * The dine-in landing declares no back button and no title (`show: true,
   * showBack: false`), which left the account chip as its only possible
   * content — and that chip is delivery-only. On a table token every slot was
   * therefore empty and the guest got a blank ~68px band above the hero, with
   * the landing padding itself down by --tc-app-header-h to clear a bar that
   * showed nothing. Every other route sets a back button or a title, so this
   * only ever suppresses a bar that would have been empty.
   */
  readonly showHeader = computed(() => {
    const route = this.routeHeader();
    const token = this.publicToken();
    if (!route.show || !token) return false;
    const cfg = this.headerCfg();
    return cfg.showBack || !!cfg.title || !!cfg.subtitle
      || accountChipVisible(token, this.fulfilment.deliveryEnabled());
  });

  /** Merge the static route header with any dynamic per-screen patch. */
  readonly headerCfg = computed(() => {
    const r = this.routeHeader();
    const dyn = this.headerConfigSvc.config();
    return {
      showBack: r.showBack,
      backAriaLabel: dyn?.backAriaLabel ?? r.backAriaLabel,
      title: dyn?.title !== undefined ? dyn.title : r.title,
      subtitle: dyn?.subtitle !== undefined ? dyn.subtitle : r.subtitle,
      backHref: dyn?.backHref !== undefined ? dyn.backHref : r.backHref,
    };
  });

  ngOnInit(): void {
    // Cold load — the initial URL may already be a /t/:token/... route.
    this.applyForUrl(this.router.url);
    this.readRouteHeaderData();

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        // Clear the previous screen's dynamic header config BEFORE the next
        // component's ngOnInit runs (NavigationStart precedes activation), so a
        // screen can set its own title/back in ngOnInit without it being wiped.
        if (event instanceof NavigationStart) {
          this.headerConfigSvc.clear();
        } else if (event instanceof NavigationEnd) {
          this.applyForUrl(event.urlAfterRedirects);
          this.readRouteHeaderData();
        }
      });
  }

  /** Bind the active store token to theme + session + profile cache, and track
   *  it for the header. URLs outside `/t/...` (404) clear the token → no header. */
  private applyForUrl(url: string): void {
    const path = url.split(/[?#]/)[0];
    const match = path.match(/^\/t\/([^/]+)/);
    if (match && match[1]) {
      const token = decodeURIComponent(match[1]);
      this.theme.bindToken(token);
      this.googleTag.bindToken(token);
      this.session.bindToken(token);
      this.publicToken.set(token);
      this.profile.ensureLoaded(token);
      // #4 — warm the fulfilment config app-wide so the header account chip can
      // gate "Sign in" on delivery being enabled (customer accounts are a
      // delivery-only feature). Cache-served when warm; dark-by-default until it
      // resolves, so no Sign-in ever flashes on a delivery-off store.
      this.fulfilment.getConfig(token).subscribe();
    } else {
      // Outside store scope (the tenant store selector at /s/:tenantToken, or
      // the 404). Drop the last store's brand colour so it doesn't bleed onto
      // a tenant-level screen listing several stores.
      this.theme.unbind();
      this.publicToken.set(null);
    }
  }

  /** Read `data.header` from the deepest activated route. */
  private readRouteHeaderData(): void {
    let r: ActivatedRouteSnapshot = this.router.routerState.snapshot.root;
    while (r.firstChild) r = r.firstChild;
    const h = r.data?.['header'] as Partial<RouteHeader> | undefined;
    this.routeHeader.set(h ? { ...NO_HEADER, ...h } : NO_HEADER);
  }

  onHeaderBack(): void {
    const token = this.publicToken();
    const backHref = this.headerCfg().backHref;
    if (token && backHref) {
      this.router.navigate(['/t', token, ...backHref]);
      return;
    }
    history.back();
  }
}
